import { Alert, Button, Header, Modal, Text } from '@bbl-digital/snorre'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ActionButtons, ModalWrapper, Wrapper } from './styles'
import CollapsedMessage from '../CollapsedMessage'
import {
  getIsSmsCollapsed,
  getSendSms,
} from 'shared/store/sendMessage/sms/selectors'
import {
  SEND_SMS_DELETE_DRAFT_REQUESTED,
  SEND_SMS_FETCH_SETTINGS_REQUESTED,
  SEND_SMS_IS_COLLAPSED,
  SEND_SMS_RESET,
} from 'shared/store/sendMessage/sms/actionTypes'
import SendSmsForm from './SendSmsForm'
import { isSendAllowed } from '../shared/utils/send-sms-helper'
import SkeletonInput from 'shared/components/skeletons/SkeletonInput'
import { SMS_FETCH_NEW_SILENTLY_REQUESTED } from 'shared/store/communication/smsMessages/actionTypes'
import { InboxSectionEnum } from 'Modules/Communication/shared/models/Inbox'
import { getBoard } from 'shared/store/board/selectors'

interface IProps {
  onSend: (message: string, id: string) => void
  onClose: () => void
}

const SendSms: FC<IProps> = ({ onClose, onSend }) => {
  const {
    message,
    messageId,
    processing,
    error,
    isDraftUpdated,
    isEditDraft,
    settings,
  } = useSelector(getSendSms)
  const { loading: boardLoading } = useSelector(getBoard)
  const isCollapsed = useSelector(getIsSmsCollapsed)
  const [showSaveDraft, setShowSaveDraft] = useState(false)
  const [ready, setReady] = useState(false)
  const dispatch = useDispatch()

  const sendNotAllowed = settings.data && !isSendAllowed(settings.data)
  const isLoading = boardLoading || settings.loading

  useEffect(() => {
    dispatch({
      type: SEND_SMS_FETCH_SETTINGS_REQUESTED,
    })
    setReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = (withDelete?: boolean) => () => {
    if (withDelete && messageId) deleteDraft()
    else
      dispatch({
        type: SMS_FETCH_NEW_SILENTLY_REQUESTED,
        payload: { section: InboxSectionEnum.Draft },
      })
    dispatch({
      type: SEND_SMS_RESET,
    })
    onClose()
  }

  const handleCollapsedMessage = (value: boolean) => {
    dispatch({
      type: SEND_SMS_IS_COLLAPSED,
      payload: value,
    })
  }

  const deleteDraft = () => {
    dispatch({
      type: SEND_SMS_DELETE_DRAFT_REQUESTED,
      payload: messageId,
    })
  }

  const handleCloseClicked = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isEditDraft
      ? handleClose(false)()
      : isDraftUpdated
      ? setShowSaveDraft(true)
      : handleClose(true)()
  }

  return (
    <>
      {showSaveDraft ? (
        <Modal
          open
          header={<Header level={4}>Lagre utkast</Header>}
          width="450px"
          onClose={() => setShowSaveDraft(false)}
        >
          <Text>Vil du lagre utkastet?</Text>
          <ActionButtons>
            <Button outline onClick={handleClose(true)}>
              Nei
            </Button>
            <Button onClick={handleClose()}>Ja</Button>
          </ActionButtons>
        </Modal>
      ) : (
        <>
          {isCollapsed && (
            <CollapsedMessage
              title="Ny SMS"
              type="sms"
              onClose={() => handleCloseClicked()}
              onOpen={() => handleCollapsedMessage(false)}
            />
          )}
          <ModalWrapper collapsed={isCollapsed}>
            <Modal
              open
              disableLocalClose
              disableScroll={!isCollapsed}
              onCollapse={() => handleCollapsedMessage(true)}
              width="750px"
              onClose={handleCloseClicked}
              header={<Header level={4}>Ny SMS</Header>}
            >
              <Wrapper>
                {(isLoading || !ready) && (
                  <>
                    <SkeletonInput />
                    <SkeletonInput inputHeight="186px" />
                  </>
                )}

                {(settings.error || sendNotAllowed) && (
                  <Alert danger>
                    {settings.error ||
                      'Maksgrense for antall sendte SMS er nådd'}
                  </Alert>
                )}
                {!settings.error &&
                  settings.data &&
                  !isLoading &&
                  ready &&
                  !sendNotAllowed && (
                    <SendSmsForm
                      message={message}
                      processing={processing}
                      error={error}
                      messageId={messageId}
                      settings={settings.data}
                      onSubmit={(e) => onSend(e, messageId || '')}
                      onSaveDraft={handleClose()}
                    />
                  )}
              </Wrapper>
            </Modal>
          </ModalWrapper>
        </>
      )}
    </>
  )
}

export default SendSms
